import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Divider,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from "@mui/material";
import Fuse from "fuse.js";
import { matchSorter } from "match-sorter";
import { Document } from "flexsearch";
function SelectedItemList({
  items = [],
  searchTerm = "",
  selectedColors = [],
  selectedLines = [],
  handleQuantityChange,
  multiplier,
  onAddToVisibleRows,
  visibleRows,
}) {
  const escapeRegex = (str) =>
    str?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") || "";

  // ✅ Clean, safe, searchable item list
  const validItems = useMemo(() => {
    return Array.isArray(items)
      ? items
          .filter((i) => i?.id && i?.product)
          .map((i) => ({
            ...i,
            product: i.product || "",
            description: i.description || "",
            color: i.color || "",
            line: i.line || "",
          }))
      : [];
  }, [items]);

  // ✅ FlexSearch index
  const flexIndex = useMemo(() => {
    const index = new Document({
      tokenize: "forward",
      document: {
        id: "id",
        index: ["product", "description", "color", "line"],
      },
    });

    validItems.forEach((item) => {
      index.add(item);
    });

    return index;
  }, [validItems]);

  // ✅ Filter by color and line after search
  const filteredItems = useMemo(() => {
    const safeSearchTerm = escapeRegex(searchTerm?.trim().toLowerCase());

    let baseResults = [];

    if (safeSearchTerm && validItems.length > 0) {
      try {
        baseResults = matchSorter(validItems, safeSearchTerm, {
          keys: ["product", "description", "color", "line"],
        }).slice(0, 25);
      } catch (e) {
        console.warn("matchSorter failed:", e);
      }

      if (baseResults.length === 0) {
        try {
          const fuse = new Fuse(validItems, {
            keys: [
              { name: "product", weight: 0.6 },
              { name: "description", weight: 0.3 },
              { name: "color", weight: 0.1 },
              { name: "line", weight: 0.1 },
            ],
            threshold: 0.4,
            includeScore: true,
          });
          baseResults = fuse
            .search(safeSearchTerm)
            .map((r) => r.item)
            .slice(0, 25);
        } catch (e) {
          console.warn("Fuse.js failed:", e);
        }
      }

      if (baseResults.length === 0) {
        try {
          const results = flexIndex.search(safeSearchTerm, { enrich: true });
          const flatIds = results.flatMap((r) => r.result || []);
          const uniqueIds = [...new Set(flatIds)];
          baseResults = uniqueIds
            .map((id) => validItems.find((item) => item.id === id))
            .filter(Boolean)
            .slice(0, 25);
        } catch (e) {
          console.warn("FlexSearch failed:", e);
        }
      }
    }

    return baseResults.filter((item) => {
      const matchesColor =
        selectedColors.length === 0 || selectedColors.includes(item.color);
      const matchesLine =
        selectedLines.length === 0 || selectedLines.includes(item.line);
      return matchesColor && matchesLine;
    });
  }, [searchTerm, selectedColors, selectedLines, validItems, flexIndex]);

  const handleAssemblyChange = (id, checked) => {
    const index = items.findIndex((item) => item.id === id);
    if (index !== -1) {
      const item = items[index];
      const qty = Math.max(parseInt(item.quantity || "1", 10), 1);
      handleQuantityChange?.(index, qty, checked, item.hingeDirection || "");
    }
  };

  const handleQtyChange = (id, value, assemblySelected, hingeDirection) => {
    const index = items.findIndex((item) => item.id === id);
    if (index !== -1) {
      handleQuantityChange?.(index, value, assemblySelected, hingeDirection);
    }
  };

  const handleQtyBlur = (id, quantity, assemblySelected, hingeDirection) => {
    const index = items.findIndex((item) => item.id === id);
    if (index !== -1) {
      const fixedQty = Math.max(parseInt(quantity || "1", 10), 1);
      handleQuantityChange?.(index, fixedQty, assemblySelected, hingeDirection);
    }
  };

  const handleHingeDirectionChange = (id, direction) => {
    const index = items.findIndex((item) => item.id === id);
    if (index !== -1) {
      const item = items[index];
      const qty = Math.max(parseInt(item.quantity || "1", 10), 1);
      handleQuantityChange?.(index, qty, item.assemblySelected, direction);
    }
  };

  if (filteredItems.length === 0) {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="body2" color="text.secondary">
          No matching items.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        zIndex: 2000,
        backgroundColor: "#fff",
        border: "1px solid #ccc",
        borderRadius: 1,
        boxShadow: 3,
        maxHeight: "65vh",
        overflowY: "auto",
      }}
    >
      {filteredItems.map((item, idx) => {
        const rawQty = item.quantity ?? "1";
        const parsedQty = Math.max(parseInt(rawQty || "1", 10), 1);

        const msrp = parseFloat(item.msrp?.replace(/[$,]/g, "") || "0");
        const assembly = parseFloat(item.assembly?.replace(/[$,]/g, "") || "0");
        const assemblySelected = item.assemblySelected || false;
        const hingeDirection = item.hingeDirection || "";

        const price =
          (msrp * parseFloat(multiplier || 1) +
            (assemblySelected ? assembly : 0)) *
          parsedQty;

        return (
          <Box key={item.id} sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2">
              {item.product} - {item.line} - {item.color}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.description}
            </Typography>

            <TextField
              label="Qty"
              type="number"
              size="small"
              value={rawQty}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*$/.test(val)) {
                  handleQtyChange?.(
                    item.id,
                    val,
                    assemblySelected,
                    hingeDirection
                  );
                }
              }}
              onBlur={() =>
                handleQtyBlur(item.id, rawQty, assemblySelected, hingeDirection)
              }
              sx={{ mt: 1, width: "100px" }}
              inputProps={{ min: 1 }}
            />

            {item.assembly && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={assemblySelected}
                    onChange={(e) =>
                      handleAssemblyChange(item.id, e.target.checked)
                    }
                    sx={{
                      color: "black",
                      "&.Mui-checked": {
                        color: "black",
                      },
                    }}
                  />
                }
                label={`Add Assembly (+$${assembly}/Cabinet)`}
                sx={{ ml: 1 }}
              />
            )}

            {/* Hinge Direction Toggle (only if directional and assembly selected) */}
            {item.hinge === "directional" && assemblySelected && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Select hinge direction:
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={hingeDirection}
                  onChange={(_, newDirection) => {
                    if (newDirection !== null) {
                      handleHingeDirectionChange(item.id, newDirection);
                    }
                  }}
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "uppercase", // optional, to match the look in image
                      color: "black", // unselected text
                      borderColor: "#ccc", // default border
                      "&.Mui-selected": {
                        backgroundColor: "#fdecea", // light red background
                        color: "#d32f2f", // red text
                        borderColor: "#f28b82", // optional: red-ish border
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "#f9d5d3",
                      },
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    },
                  }}
                >
                  <ToggleButton value="left">Left</ToggleButton>
                  <ToggleButton value="right">Right</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
              Price: ${price.toFixed(2)}
            </Typography>
            {visibleRows?.[item.id] ? (
              <Typography variant="body2" sx={{ mt: 1, color: "green" }}>
                ✅ Already in the Quote List
              </Typography>
            ) : (
              <Button
                variant="contained"
                color="error"
                size="small"
                sx={{ mt: 1 }}
                onClick={() => onAddToVisibleRows?.(item)}
              >
                Add to List
              </Button>
            )}

            {idx !== filteredItems.length - 1 && <Divider sx={{ mt: 1 }} />}
          </Box>
        );
      })}
    </Box>
  );
}

export default SelectedItemList;
